import { CalendarOutlined } from "@ant-design/icons"
import { Employee, Expense } from "@finway-group/shared/lib/models"
import { getApprovalProcessScopeFromExpenseStatus } from "@finway-group/shared/lib/utils/approvalProcess.utils"
import { Button, Tooltip } from "antd"
import moment from "moment"
import React, { useMemo } from "react"
import { X as CancelIcon, Edit2 as EditIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { AdditionalExpenseOptionButton } from "~Components/additionalExpenseOptionsButton"
import { ApprovalProcessIndicatorMenu } from "~Components/approvalProcessesIndicator"
import { ExpenseSwitching } from "~Components/ExpenseSwitching"
import { ExpenseTagSection } from "~Components/ExpenseTagSection"
import { ExportDropDownMenu } from "~Components/exportDropDownMenu"
import { ExpenseReminderModal } from "~Components/modals/expenseReminder.modal"
import { ExpenseExport } from "~Features/export/expenseExport"
import { useModal } from "~Shared/context/modal.context"
import { useCompany } from "~Shared/hooks/company.hooks"
import { useExpenseSwitching } from "~Shared/hooks/expense.hooks"
import { AuthzService, ExpenseService } from "~Shared/services"
import { deleteReminder } from "~Shared/store/actions/expense/expenseActions"
import { getExpenseReminderForUser, isExpenseExportable, isExpenseSwitchingShown } from "~Shared/utils/expense.utils"
import { getTooltipPopupContainer } from "~Shared/utils/helper.utils"

interface ActionButtonsProps {
    activeExpense: Expense
    loggedInUser: Employee
    setIsShowingImportProtocolModal: React.Dispatch<React.SetStateAction<boolean>>
    setShowDiscountForm: React.Dispatch<React.SetStateAction<boolean>>
    setIsEditModalShowing: React.Dispatch<React.SetStateAction<boolean>>
    onTagChange: () => void
}

export const ActionButtons = ({ activeExpense, loggedInUser, setIsShowingImportProtocolModal, setShowDiscountForm, setIsEditModalShowing, onTagChange }: ActionButtonsProps) => {
    const expenseId = activeExpense.id
    const expenseStatusMemoized = useMemo(() => activeExpense.status, [activeExpense.id])
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { showModal } = useModal()
    const { taggingEnabled } = useCompany()

    const { prevExpense, nextExpense, isExpenseSwitchingLoading, hasError, adjacentExpenseLabel } = useExpenseSwitching(
        activeExpense.id,
        expenseStatusMemoized,
        activeExpense.requestedBy?._id,
        activeExpense,
    )

    const editButtonView = ExpenseService.calculateExpenseEditButtonView(activeExpense, loggedInUser, t)
    const approvalProcessScope = getApprovalProcessScopeFromExpenseStatus(activeExpense.status, true)
    const reminder = getExpenseReminderForUser(activeExpense.reminders, loggedInUser.id)
    const isLoggedInUserGlobalApprover = AuthzService.isLoggedInUserGlobalApprover()
    const showExportButton = isExpenseExportable(activeExpense, loggedInUser, isLoggedInUserGlobalApprover)
    const showExpenseSwitching = useMemo(() => isExpenseSwitchingShown(activeExpense), [activeExpense.id])
    const onlyFileExport = ExpenseService.onlyAllowFileExports(activeExpense)
    return (
        <div className="flex-wrap flex">
            {taggingEnabled && <ExpenseTagSection expenseId={activeExpense.id} expenseTags={activeExpense.expenseTags} onTagChange={onTagChange} />}
            {reminder && (
                <Tooltip placement="bottom" overlayStyle={{ maxWidth: "100%" }} title={t("tooltips:expense_reminder", { date: moment(reminder.date).format("DD/MM/YYYY") })}>
                    <Button
                        className="m-5 rounded-lg bg-white border-solid font-normal text-gray-800  border-gray-300 border-1"
                        onClick={() => {
                            showModal(ExpenseReminderModal, true, { isShowing: true, expenseId, reminder })
                        }}
                    >
                        <CalendarOutlined />
                        <span className="font-medium text-text">{moment(reminder.date).format("DD/MM/YYYY")} </span>
                        <CancelIcon
                            className="ml-3"
                            onClick={(e) => {
                                dispatch(deleteReminder(expenseId))
                                e.stopPropagation()
                            }}
                        />
                    </Button>
                </Tooltip>
            )}

            {editButtonView.buttonVisible && (
                <>
                    <ApprovalProcessIndicatorMenu approvalProcesses={activeExpense.approvalProcesses} scope={approvalProcessScope} />
                    <Tooltip
                        getPopupContainer={getTooltipPopupContainer}
                        {...(editButtonView.buttonDisabled ? {} : { visible: false })}
                        title={<div>{editButtonView.reasonToDisplay}</div>}
                        placement="bottomLeft"
                    >
                        <Button className="m-5 btn-default" disabled={editButtonView.buttonDisabled} onClick={() => setIsEditModalShowing(true)}>
                            <EditIcon />
                            <span>{t("action:edit")}</span>
                        </Button>
                    </Tooltip>
                </>
            )}
            {/** We always show the export dropdown when the expense is archived to allow the user to export. */}
            {showExportButton && (
                <ExpenseExport
                    render={({ onExport, isExporting }) => (
                        <ExportDropDownMenu
                            onExport={onExport}
                            expenses={[activeExpense]}
                            isLoading={isExporting}
                            buttonClassName={"m-5"}
                            includeSepa
                            onlyFileExport={onlyFileExport}
                            isAuditTrailVisible={true}
                        />
                    )}
                />
            )}
            <AdditionalExpenseOptionButton
                expense={activeExpense}
                isEditDisabled={editButtonView.buttonDisabled}
                setIsShowingImportProtocolModal={setIsShowingImportProtocolModal}
                onOpenDiscountForm={() => setShowDiscountForm(true)}
            />
            {showExpenseSwitching && (
                <ExpenseSwitching
                    hasError={hasError}
                    isLoading={isExpenseSwitchingLoading}
                    prevExpense={prevExpense}
                    nextExpense={nextExpense}
                    adjacentExpenseLabel={adjacentExpenseLabel}
                />
            )}
        </div>
    )
}
